import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, Card, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, CircularProgress, Alert,
  Menu, MenuItem,
  Snackbar, Tooltip, Chip, TablePagination, alpha, Collapse, TextField, Divider, Stack,
  InputBase, FormControl, Select, InputLabel,
  useTheme, useMediaQuery
} from '@mui/material';
import {
  Plus, MoreVertical, Trash2, Edit, Lightbulb, RefreshCw,
  ChevronDown, ChevronUp, Search, ExternalLink, Globe, X, Tag, MapPin
} from 'lucide-react';
import axios from '../utils/axios';
import CustomPageModal from '../components/CustomPageModal';
import PageHeader from '../components/common/PageHeader';
import ConfirmDialog from '../components/common/ConfirmDialog';
import EmptyState from '../components/common/EmptyState';
import ExportTableButton from '../components/common/ExportTableButton';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCategories, fetchSubCategories, fetchCities } from '../redux/slices/businessSlice';
import { fetchAdminBrands } from '../redux/slices/catalogSlice';

const CustomPagesView = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Load catalog entities from Redux
  const { categories = [], subCategories = [], cities = [] } = useSelector((state) => state.business);
  const { brands } = useSelector((state) => state.catalog);
  const brandList = useMemo(() => brands?.data || brands || [], [brands]);

  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  // Search & Filter state for pages table
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState(''); // 'buy', 'sell', or ''

  // Modal & Row actions
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [prefillData, setPrefillData] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [actionPage, setActionPage] = useState(null);

  // Suggested pages (Only Buy pages as requested)
  const [suggestions, setSuggestions] = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestError, setSuggestError] = useState(null);
  const [minProducts, setMinProducts] = useState(5);
  const [showSuggestions, setShowSuggestions] = useState(true);

  // Delete Confirm Dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ── Load Custom Pages from API ──────────────────────────────────────────
  const loadPages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`/buy-and-sell/admin/all?page=${page + 1}&limit=${rowsPerPage}`);
      setPages(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to fetch custom pages');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    loadPages();
  }, [loadPages]);

  // ── Fetch metadata for Category, Subcategory, Brands & Cities ───────────
  useEffect(() => {
    dispatch(fetchCategories());
    dispatch(fetchSubCategories());
    dispatch(fetchCities());
    dispatch(fetchAdminBrands({ page: 1, limit: 1000, search: '' }));
  }, [dispatch]);

  // ── Load Buy Page Suggestions (Strictly Buy Pages) ──────────────────────
  const loadSuggestions = useCallback(async () => {
    setSuggestLoading(true);
    setSuggestError(null);
    try {
      const res = await axios.get(`/buy-and-sell/admin/suggestions?minProducts=${minProducts}`);
      // Strictly enforce buy pages only
      const buyOnly = (res.data.data || []).filter((s) => s.buy_sell === 'buy');
      setSuggestions(buyOnly);
    } catch (err) {
      setSuggestError(err.response?.data?.msg || 'Failed to load suggestions');
    } finally {
      setSuggestLoading(false);
    }
  }, [minProducts]);

  useEffect(() => {
    loadSuggestions();
  }, [loadSuggestions]);

  // ── Suggestion → prefilled "Create Page" helpers ──────────────────────────
  const slugify = (s) =>
    String(s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

  const prettyCity = (c) =>
    String(c || '')
      .split(/[-\s]+/)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');

  const buildPrefill = (s) => {
    const cityLabel = prettyCity(s.city);
    const cat = s.category?.category_name || '';
    return {
      page_name: `Buy ${cat} in ${cityLabel}`,
      slug: slugify(`buy-${cat}-in-${s.city}`),
      category: s.category?._id || '',
      sub_category: '',
      brand: '',
      city: s.city,
      buy_sell: 'buy', // Always Buy page
      meta_title: `Buy ${cat} in ${cityLabel} | Reseller Bazzar`,
      meta_description: `Buy ${cat} in ${cityLabel} on Reseller Bazzar. Browse ${s.productCount}+ verified listings and connect directly with local sellers — no broker or listing fees.`,
      keywords: `${cat} ${cityLabel}, buy ${cat} ${cityLabel}, ${cat} dealers ${cityLabel}, reseller ${cat} ${cityLabel}`,
      meta_content: [],
    };
  };

  const handleCreateSuggestion = (s) => {
    setEditingPage(null);
    setPrefillData(buildPrefill(s));
    setIsModalOpen(true);
  };

  // ── Row Action Handlers ──────────────────────────────────────────────────
  const handleMenuOpen = (event, pageData) => {
    setMenuAnchorEl(event.currentTarget);
    setActionPage(pageData);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setActionPage(null);
  };

  const handleEditClick = () => {
    setEditingPage(actionPage);
    setPrefillData(null);
    setIsModalOpen(true);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setMenuAnchorEl(null);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      if (actionPage) {
        await axios.delete(`/buy-and-sell/${actionPage._id}`, { skipNotify: 'success' });
        setSnackbar({ open: true, message: 'Custom page deleted successfully', severity: 'success' });
        loadPages();
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.msg || 'Failed to delete page', severity: 'error' });
    }
    setDeleteDialogOpen(false);
    setActionPage(null);
  };

  // ── Client-side search and filtering for table rows ──────────────────────
  const filteredPages = useMemo(() => {
    return pages.filter((p) => {
      const matchSearch =
        !searchTerm ||
        (p.page_name && p.page_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (p.slug && p.slug.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (p.city && p.city.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (p.brand && p.brand.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchType = !typeFilter || p.buy_sell === typeFilter;
      return matchSearch && matchType;
    });
  }, [pages, searchTerm, typeFilter]);

  return (
    <Box sx={{ pb: 6 }}>
      {/* ── Page Header ─────────────────────────────────────────────────── */}
      <PageHeader
        title="Custom SEO Pages"
        subtitle="Manage search engine landing pages, keyword-optimized catalogs, and high-converting city buy pages."
        count={total}
        countLabel="pages"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Engagement', path: '/custom-pages' },
          { label: 'SEO Pages' },
        ]}
        action={
          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={() => {
              setEditingPage(null);
              setPrefillData(null);
              setIsModalOpen(true);
            }}
          >
            New SEO Page
          </Button>
        }
        secondaryAction={
          <ExportTableButton
            data={pages}
            total={total}
            onExportAll={async () => {
              const res = await axios.get('/buy-and-sell/admin/all', { params: { all: true } });
              return res.data.data || [];
            }}
            columns={[
              { label: 'Page Title', key: 'title' },
              { label: 'Type', key: 'type' },
              { label: 'Slug', key: 'slug' },
              { label: 'Meta Title', key: 'meta_title' },
              { label: 'Status', value: (r) => r.is_active ? 'Active' : 'Inactive' },
              { label: 'Created At', value: (r) => r.createdAt ? new Date(r.createdAt).toLocaleString() : 'N/A' },
            ]}
            filename="custom_seo_pages"
          />
        }
      />

      {/* ── Suggested Buy Pages (Buy-Only SEO Opportunities) ─────────────── */}
      <Card
        sx={{
          borderRadius: 3.5,
          mb: 3.5,
          border: (theme) => `1px solid ${alpha(theme.palette.success.main, 0.25)}`,
          bgcolor: (theme) => alpha(theme.palette.success.main, 0.03),
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2.5,
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                p: 1.2,
                borderRadius: 2.5,
                bgcolor: (theme) => alpha(theme.palette.success.main, 0.15),
                color: 'success.dark'
              }}
            >
              <Lightbulb size={22} />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  Suggested Buy Pages
                </Typography>
                <Chip
                  label="BUY ONLY"
                  size="small"
                  sx={{
                    height: 20,
                    fontWeight: 800,
                    fontSize: 10,
                    bgcolor: (theme) => alpha(theme.palette.success.main, 0.15),
                    color: 'success.dark'
                  }}
                />
              </Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                High-demand Category × City combinations ready for dedicated Buy landing pages
              </Typography>
            </Box>
            {!suggestLoading && (
              <Chip
                label={`${suggestions.length} available`}
                size="small"
                sx={{
                  fontWeight: 700,
                  bgcolor: (theme) => alpha(theme.palette.success.main, 0.15),
                  color: 'success.dark',
                  display: { xs: 'none', md: 'inline-flex' }
                }}
              />
            )}
          </Box>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <TextField
              label="Min products"
              type="number"
              size="small"
              value={minProducts}
              onChange={(e) => setMinProducts(Math.max(1, parseInt(e.target.value, 10) || 1))}
              inputProps={{ min: 1, style: { width: 65, textAlign: 'center' } }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}
            />
            <Tooltip title="Refresh suggestions">
              <IconButton
                aria-label="Refresh suggestions"
                onClick={loadSuggestions}
                disabled={suggestLoading}
                size="small"
                sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}
              >
                <RefreshCw size={16} />
              </IconButton>
            </Tooltip>
            <IconButton
              aria-label={showSuggestions ? 'Collapse suggestions' : 'Expand suggestions'}
              onClick={() => setShowSuggestions((v) => !v)}
              size="small"
              sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}
            >
              {showSuggestions ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </IconButton>
          </Stack>
        </Box>

        <Collapse in={showSuggestions}>
          <Divider />
          {suggestLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} thickness={4} sx={{ color: 'success.main' }} />
            </Box>
          ) : suggestError ? (
            <Box sx={{ p: 2.5 }}><Alert severity="error">{suggestError}</Alert></Box>
          ) : suggestions.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No pending Buy page suggestions. Every Category × City combo with ≥ {minProducts} active products already has a page! 🎉
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ overflowX: 'auto', maxHeight: 360 }}>
              <Table stickyHeader size="small" sx={{ minWidth: 600 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: 'text.secondary', fontWeight: 700, bgcolor: 'background.paper' }}>Category</TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontWeight: 700, bgcolor: 'background.paper' }}>City</TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontWeight: 700, bgcolor: 'background.paper' }}>Active Products</TableCell>
                    <TableCell align="right" sx={{ bgcolor: 'background.paper' }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {suggestions.map((s, i) => (
                    <TableRow key={`${s.category?._id}-${s.city}-${i}`} hover>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Tag size={16} color="#00AB55" />
                          <Typography variant="body2" fontWeight={600}>{s.category?.category_name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', textTransform: 'capitalize' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <MapPin size={14} color="#637381" />
                          {prettyCity(s.city)}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${s.productCount} products`}
                          size="small"
                          sx={{ fontWeight: 700, bgcolor: alpha('#00AB55', 0.1), color: 'success.dark', borderRadius: 1 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<Plus size={14} />}
                          onClick={() => handleCreateSuggestion(s)}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 700,
                            bgcolor: 'success.main',
                            '&:hover': { bgcolor: 'success.dark' },
                            fontSize: '0.75rem',
                            py: 0.5,
                            px: 1.8
                          }}
                        >
                          Create Buy Page
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Collapse>
      </Card>

      {/* ── Main Custom Pages Table Card ─────────────────────────────────── */}
      <Card sx={{ borderRadius: 3.5, overflow: 'hidden', position: 'relative', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
        {loading && (
          <Box
            sx={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.6),
              zIndex: 10, backdropFilter: 'blur(2px)'
            }}
          >
            <CircularProgress thickness={4} size={40} sx={{ color: 'primary.main' }} />
          </Box>
        )}

        {/* Toolbar: Search, Type Filter, Reset */}
        <Box
          sx={{
            p: 2.5,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper'
          }}
        >
          {/* Search Box */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              borderRadius: 2,
              px: 1.5,
              py: 0.6,
              border: '1px solid',
              borderColor: 'divider',
              width: { xs: '100%', sm: 360 },
              '&:focus-within': { borderColor: 'primary.main', boxShadow: '0 0 0 2px rgba(0, 171, 85, 0.12)' }
            }}
          >
            <Search size={18} color="#637381" />
            <InputBase
              placeholder="Search by page name, slug, city..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value.trimStart())}
              sx={{ ml: 1, flex: 1, fontSize: '0.875rem' }}
            />
            {searchTerm && (
              <IconButton size="small" onClick={() => setSearchTerm('')} sx={{ p: 0.5 }}>
                <X size={14} />
              </IconButton>
            )}
          </Box>

          {/* Type Filter & Reset */}
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', justifyContent: { xs: 'space-between', sm: 'flex-end' } }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                label="Type"
                onChange={(e) => setTypeFilter(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="buy">Buy Pages</MenuItem>
                <MenuItem value="sell">Sell Pages</MenuItem>
              </Select>
            </FormControl>

            {(searchTerm || typeFilter) && (
              <Button
                size="small"
                onClick={() => { setSearchTerm(''); setTypeFilter(''); }}
                sx={{ textTransform: 'none', fontWeight: 600, color: 'error.main' }}
              >
                Reset
              </Button>
            )}
          </Box>
        </Box>

        {error ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: { xs: 600, sm: 750 } }}>
                <TableHead sx={{ backgroundColor: 'background.neutral' }}>
                  <TableRow>
                    <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Page Details</TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Category & Targeting</TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>City</TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Type</TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 700 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPages.length > 0 ? (
                    filteredPages.map((pageData) => {
                      const categoryDoc = categories.find((c) => c._id === pageData.category || c._id === pageData.category?._id);
                      const catName = categoryDoc?.category_name || (typeof pageData.category === 'object' ? pageData.category?.category_name : pageData.category) || '—';
                      const publicUrl = `https://www.resellerbazzar.in/${pageData.buy_sell || 'buy'}/${pageData.slug}`;

                      return (
                        <TableRow key={pageData._id} hover>
                          {/* Page Name & Slug */}
                          <TableCell sx={{ maxWidth: 300 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.3 }}>
                              {pageData.page_name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.3 }}>
                              /{pageData.buy_sell || 'buy'}/{pageData.slug}
                            </Typography>
                            {pageData.meta_title && (
                              <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', noWrap: true, textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                {pageData.meta_title}
                              </Typography>
                            )}
                          </TableCell>

                          {/* Category, Subcategory & Brand */}
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6, alignItems: 'flex-start' }}>
                              <Chip
                                size="small"
                                label={catName}
                                sx={{
                                  height: 22,
                                  fontSize: 11,
                                  fontWeight: 700,
                                  bgcolor: (theme) => alpha(theme.palette.info.main, 0.1),
                                  color: 'info.dark',
                                  borderRadius: 1
                                }}
                              />
                              {pageData.sub_category && (
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
                                  Subcat: <strong>{pageData.sub_category}</strong>
                                </Typography>
                              )}
                              {pageData.brand && (
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
                                  Brand: <strong>{pageData.brand}</strong>
                                </Typography>
                              )}
                            </Box>
                          </TableCell>

                          {/* City */}
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                              <MapPin size={15} color="#637381" />
                              <Typography variant="body2" sx={{ textTransform: 'capitalize', fontWeight: 600 }}>
                                {pageData.city || '—'}
                              </Typography>
                            </Box>
                          </TableCell>

                          {/* Type (BUY / SELL) */}
                          <TableCell>
                            <Chip
                              label={(pageData.buy_sell || 'buy').toUpperCase()}
                              size="small"
                              sx={{
                                fontWeight: 800,
                                fontSize: 11,
                                height: 24,
                                backgroundColor: (theme) =>
                                  pageData.buy_sell === 'buy'
                                    ? alpha(theme.palette.success.main, 0.12)
                                    : alpha(theme.palette.error.main, 0.12),
                                color: pageData.buy_sell === 'buy' ? 'success.dark' : 'error.dark',
                                borderRadius: 1
                              }}
                            />
                          </TableCell>

                          {/* Actions */}
                          <TableCell align="right">
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                              <Tooltip title="View Live Page">
                                <IconButton
                                  size="small"
                                  component="a"
                                  href={publicUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                                >
                                  <ExternalLink size={16} />
                                </IconButton>
                              </Tooltip>

                              <IconButton
                                aria-label="Row actions"
                                size="small"
                                onClick={(e) => handleMenuOpen(e, pageData)}
                                sx={{ color: 'text.secondary' }}
                              >
                                <MoreVertical size={18} />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ p: 0 }}>
                        <EmptyState
                          icon={Globe}
                          title={searchTerm || typeFilter ? 'No custom pages match your criteria' : 'No custom pages found'}
                          description={searchTerm || typeFilter ? 'Try clearing or modifying your search filters.' : 'Create search-engine optimized buy/sell pages to capture organic traffic.'}
                          actionLabel={searchTerm || typeFilter ? undefined : 'New SEO Page'}
                          onAction={searchTerm || typeFilter ? undefined : () => {
                            setEditingPage(null);
                            setPrefillData(null);
                            setIsModalOpen(true);
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: { xs: 1, sm: 2 }, borderTop: 1, borderColor: 'divider' }}>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage={isMobile ? "" : "Rows per page:"}
                sx={{
                  border: 'none',
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-input': {
                    display: isMobile ? 'none' : 'block',
                  },
                }}
              />
            </Box>
          </>
        )}
      </Card>

      {/* Row Action Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{ sx: { width: 140, boxShadow: '0 8px 16px 0 rgba(145, 158, 171, 0.24)', borderRadius: 2 } }}
      >
        <MenuItem onClick={handleEditClick} sx={{ fontSize: '0.875rem', gap: 1 }}>
          <Edit size={16} /> Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main', fontSize: '0.875rem', gap: 1 }}>
          <Trash2 size={16} /> Delete
        </MenuItem>
      </Menu>

      {/* Custom Page Create/Edit Modal with Category Hierarchy */}
      <CustomPageModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        pageData={editingPage}
        prefillData={prefillData}
        categories={categories || []}
        subCategories={subCategories || []}
        brands={brandList}
        cities={cities || []}
        onSuccess={() => {
          loadPages();
          setSnackbar({
            open: true,
            message: `Page ${editingPage ? 'updated' : 'created'} successfully!`,
            severity: 'success'
          });
        }}
      />

      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Delete SEO Page"
        content={`Are you sure you want to delete "${actionPage?.page_name}"? This will remove this landing page from search engine index.`}
        confirmText="Delete"
        severity="error"
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: 2, boxShadow: '0 8px 16px 0 rgba(0,0,0,0.1)' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomPagesView;
